import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import type { RepoState, CommitNode, StagedFile, WorkingFile, RepoFile } from '@/types/git';
import { createStorage, DB_NAME, STORE_NAMES } from '@/lib/storage';
import type { GitPreset } from '@/lib/git/presets';

/** Ключ для хранения */
const STORAGE_KEY = 'git-lessons-repo';

/** Хранилище для Git состояния (IndexedDB с fallback на localStorage) */
const repoStorage: StateStorage = createStorage({
  dbName: DB_NAME,
  storeName: STORE_NAMES.REPO,
});

/** Начальное состояние репозитория */
const initialRepoState: RepoState = {
  initialized: false,
  commits: {},
  branches: {},
  head: null,
  isDetached: false,
  staging: [],
  workingDir: [],
  remotes: {},
  files: [],
};

interface GitStore {
  // State
  repo: RepoState;

  // Actions
  init: () => void;
  reset: () => void;
  /** Полная замена состояния репозитория */
  setRepoState: (state: RepoState) => void;
  /** Обновление частей состояния */
  updateRepoState: (updates: Partial<RepoState>) => void;
  /** Загрузка предустановки */
  loadPreset: (preset: GitPreset) => void;
  /** Очистка persisted состояния и сброс к начальному */
  clearPersistedState: () => Promise<void>;

  // Commit operations
  addCommit: (commit: CommitNode) => void;

  // Branch operations
  createBranch: (name: string, sha: string) => void;
  deleteBranch: (name: string) => void;
  checkout: (ref: string) => void;

  // Staging operations
  stageFile: (file: StagedFile) => void;
  unstageFile: (path: string) => void;
  clearStaging: () => void;

  // Working directory operations
  addWorkingFile: (file: WorkingFile) => void;
  removeWorkingFile: (path: string) => void;
  updateWorkingFile: (path: string, updates: Partial<WorkingFile>) => void;

  // File operations
  getFiles: () => RepoFile[];
  getFileByPath: (path: string) => RepoFile | undefined;
  updateFile: (path: string, updates: Partial<RepoFile>) => void;

  // Getters
  getCurrentBranch: () => string | null;
  getHeadCommit: () => CommitNode | null;
  getCommitHistory: () => CommitNode[];

  // Import/Export
  /** Экспорт состояния репозитория в JSON строку */
  exportState: () => string;
  /** Импорт состояния репозитория из JSON строки */
  importState: (stateJson: string) => boolean;
}

/**
 * Zustand store для состояния Git репозитория
 */
export const useGitStore = create<GitStore>()(
  persist(
    (set, get) => ({
      repo: initialRepoState,

      init: () => {
        set((state) => ({
          repo: {
            ...state.repo,
            initialized: true,
            branches: { main: '' },
            head: 'main',
            isDetached: false,
          },
        }));
      },

      reset: () => {
        set({ repo: initialRepoState });
      },

      clearPersistedState: async () => {
        // Очищаем хранилище и сбрасываем состояние
        await repoStorage.removeItem(STORAGE_KEY);
        set({ repo: initialRepoState });
      },

      setRepoState: (state) => {
        set({ repo: state });
      },

      updateRepoState: (updates) => {
        set((state) => ({
          repo: {
            ...state.repo,
            ...updates,
          },
        }));
      },

      loadPreset: (preset) => {
        // Преобразуем предустановку в состояние репозитория
        const commits: Record<string, CommitNode> = {};
        const branches: Record<string, string> = {};
        const remotes: Record<string, string> = {};
        const files: RepoFile[] = [];

        // Добавляем коммиты
        for (const commit of preset.commits) {
          commits[commit.sha] = {
            sha: commit.sha,
            parents: commit.parents,
            tree: '', // Не используется в симуляции
            message: commit.message,
            author: commit.author,
            authorEmail: commit.authorEmail,
            timestamp: commit.timestamp,
          };
        }

        // Добавляем ветки
        for (const branch of preset.branches) {
          if (!branch.isRemote) {
            branches[branch.name] = branch.head;
          }
        }

        // Добавляем remotes
        if (preset.remotes) {
          for (const remote of preset.remotes) {
            remotes[remote.name] = remote.url;
            // Добавляем remote branches
            for (const [branchName, sha] of Object.entries(remote.branches)) {
              branches[`${remote.name}/${branchName}`] = sha;
            }
          }
        }

        // Добавляем файлы с содержимым
        if (preset.files) {
          for (const file of preset.files) {
            files.push({
              path: file.path,
              content: file.content,
              status: file.status,
            });
          }
        }

        // Формируем состояние
        const newState: RepoState = {
          initialized: true,
          commits,
          branches,
          head: preset.currentBranch,
          isDetached: false,
          staging: preset.staging ?? [],
          workingDir: preset.workingDir ?? [],
          remotes,
          files,
        };

        console.log('[gitStore] loadPreset called, files count:', files.length);
        set({ repo: newState });
      },

      addCommit: (commit) => {
        set((state) => {
          const currentBranch = state.repo.head && !state.repo.isDetached ? state.repo.head : null;

          return {
            repo: {
              ...state.repo,
              commits: {
                ...state.repo.commits,
                [commit.sha]: commit,
              },
              branches: currentBranch
                ? { ...state.repo.branches, [currentBranch]: commit.sha }
                : state.repo.branches,
              head: state.repo.isDetached ? commit.sha : state.repo.head,
            },
          };
        });
      },

      createBranch: (name, sha) => {
        set((state) => ({
          repo: {
            ...state.repo,
            branches: {
              ...state.repo.branches,
              [name]: sha,
            },
          },
        }));
      },

      deleteBranch: (name) => {
        set((state) => {
          const { [name]: _, ...remainingBranches } = state.repo.branches;
          return {
            repo: {
              ...state.repo,
              branches: remainingBranches,
            },
          };
        });
      },

      checkout: (ref) => {
        set((state) => {
          const isBranch = ref in state.repo.branches;
          const sha = isBranch ? (state.repo.branches[ref] ?? null) : ref;

          return {
            repo: {
              ...state.repo,
              head: isBranch ? ref : sha,
              isDetached: !isBranch,
            },
          };
        });
      },

      stageFile: (file) => {
        set((state) => ({
          repo: {
            ...state.repo,
            staging: [...state.repo.staging.filter((f) => f.path !== file.path), file],
          },
        }));
      },

      unstageFile: (path) => {
        set((state) => ({
          repo: {
            ...state.repo,
            staging: state.repo.staging.filter((f) => f.path !== path),
          },
        }));
      },

      clearStaging: () => {
        set((state) => ({
          repo: {
            ...state.repo,
            staging: [],
          },
        }));
      },

      addWorkingFile: (file) => {
        set((state) => ({
          repo: {
            ...state.repo,
            workingDir: [...state.repo.workingDir.filter((f) => f.path !== file.path), file],
          },
        }));
      },

      removeWorkingFile: (path) => {
        set((state) => ({
          repo: {
            ...state.repo,
            workingDir: state.repo.workingDir.filter((f) => f.path !== path),
          },
        }));
      },

      updateWorkingFile: (path, updates) => {
        set((state) => ({
          repo: {
            ...state.repo,
            workingDir: state.repo.workingDir.map((f) =>
              f.path === path ? { ...f, ...updates } : f
            ),
          },
        }));
      },

      getFiles: () => {
        return get().repo.files ?? [];
      },

      getFileByPath: (path) => {
        return (get().repo.files ?? []).find((f) => f.path === path);
      },

      updateFile: (path, updates) => {
        set((state) => ({
          repo: {
            ...state.repo,
            files: (state.repo.files ?? []).map((f) =>
              f.path === path ? { ...f, ...updates } : f
            ),
          },
        }));
      },

      getCurrentBranch: () => {
        const { repo } = get();
        if (repo.isDetached || !repo.head) return null;
        return repo.head;
      },

      getHeadCommit: () => {
        const { repo } = get();
        if (!repo.head) return null;

        const sha = repo.isDetached ? repo.head : repo.branches[repo.head];
        if (!sha) return null;

        return repo.commits[sha] ?? null;
      },

      getCommitHistory: () => {
        const { repo } = get();
        const commits: CommitNode[] = [];
        const visited = new Set<string>();

        const headCommit = get().getHeadCommit();
        if (!headCommit) return [];

        const traverse = (commit: CommitNode) => {
          if (visited.has(commit.sha)) return;
          visited.add(commit.sha);
          commits.push(commit);

          for (const parentSha of commit.parents) {
            const parent = repo.commits[parentSha];
            if (parent) traverse(parent);
          }
        };

        traverse(headCommit);
        return commits;
      },

      exportState: () => {
        const { repo } = get();
        return JSON.stringify(repo);
      },

      importState: (stateJson) => {
        try {
          const state = JSON.parse(stateJson) as RepoState;
          // Базовая валидация
          if (typeof state !== 'object' || state === null) {
            return false;
          }
          if (!('initialized' in state)) {
            return false;
          }
          set({ repo: state });
          return true;
        } catch (error) {
          console.error('Failed to import git state:', error);
          return false;
        }
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => repoStorage),
    }
  )
);
